// src/services/workerRequestService.js
// Shared implementation behind the per-worker singletons in this folder.
// storageWorkerService and productionWorkerService were the same class copied,
// differing only in worker URL, request-id prefix, the reply type older workers
// use, and whether the URL carries a cache-busting version -- so they are
// configurations of this one class rather than two classes.
// ─────────────────────────────────────────────────────────────

const CANCELLED = "WorkerRequestCancelledError";

/* Bounds a request that will never be answered -- a worker that failed to load,
 * or one wedged mid-computation -- so its promise and listener do not live on
 * for the rest of the session. Deliberately far above any real workload: a full
 * year of 15-minute data across every device is seconds of work, so nothing
 * legitimate reaches this even on a badly loaded machine. A false timeout on
 * real data would be worse than the leak it guards against. */
const DEFAULT_TIMEOUT_MS = 5 * 60 * 1000;

function cancelledError(reason) {
  const error = new Error(reason);
  /* A marker property rather than an Error subclass: `instanceof` against a
   * subclassed Error does not survive transpilation reliably. */
  error.name = CANCELLED;
  return error;
}

/**
 * True for the rejection a superseded or aborted request produces. Callers that
 * re-run on every view/date change should skip logging these -- they mean "a
 * newer request took over", not "the worker failed".
 */
export function isWorkerRequestCancelled(error) {
  return error?.name === CANCELLED || error?.name === "AbortError";
}

class WorkerRequestService {
  constructor({
    scriptPath,
    requestIdPrefix,
    legacyResultType,
    version = null,
    timeoutMs = DEFAULT_TIMEOUT_MS,
  }) {
    this.scriptPath = scriptPath;
    this.requestIdPrefix = requestIdPrefix;
    this.legacyResultType = legacyResultType;
    this.version = version;
    this.timeoutMs = timeoutMs;

    this.worker = null;
    this.workerVersion = null;
    this.requestCounter = 0;
    this.inFlight = new Map(); // requestId -> cancel(reason)
    this.inFlightByKey = new Map(); // supersede key -> requestId
  }

  getWorkerScriptUrl() {
    const base = `${process.env.PUBLIC_URL || ""}${this.scriptPath}`;
    if (!this.version) return base;
    const sep = base.includes("?") ? "&" : "?";
    return `${base}${sep}v=${this.version}`;
  }

  /* Initialise the Web Worker once (recreate if a deploy bumped the version) */
  init() {
    if (this.worker && this.workerVersion === this.version) return;
    this.worker?.terminate();
    this.worker = null;
    this.workerVersion = this.version;
    this.worker = new Worker(this.getWorkerScriptUrl());
  }

  /* Destroy the worker (if you ever need to) */
  terminate() {
    this.cancelAll("Worker terminated");
    this.worker?.terminate();
    this.worker = null;
    this.workerVersion = null;
  }

  /** Cancel the in-flight request under `key`. Returns whether there was one. */
  cancel(key, reason = "Worker request cancelled") {
    const requestId = this.inFlightByKey.get(key);
    if (requestId === undefined) return false;
    this.inFlight.get(requestId)?.(reason);
    return true;
  }

  cancelAll(reason = "Worker request cancelled") {
    Array.from(this.inFlight.values()).forEach((cancel) => cancel(reason));
  }

  /**
   * Generic helper:
   *   payload = { type: 'CALCULATE_PER_DEVICE_LIST', data: { … } }
   * Returns a Promise that resolves with `result` from the worker
   * when the worker posts a message whose `type` matches.
   *
   * options.key    - supersede key. Starting a request under a key cancels the
   *                  previous one under that key, so a re-fired effect cannot
   *                  have its stale reply land after the newer one.
   * options.signal - AbortSignal, for cancelling on unmount.
   * Either way the abandoned promise rejects with isWorkerRequestCancelled().
   */
  run(payload, options = {}) {
    const { signal, key, timeoutMs = this.timeoutMs } = options;

    /* Supersede before the new request registers, or it would cancel itself. */
    if (key !== undefined) this.cancel(key, "Superseded by a newer request");

    return new Promise((resolve, reject) => {
      if (signal?.aborted) {
        reject(cancelledError("Worker request aborted before it started"));
        return;
      }

      try {
        this.init(); // ensure worker exists
      } catch (error) {
        /* `new Worker` throws where workers are unavailable. Reject rather than
         * throw out of run(), so a caller's .catch() still sees it. */
        reject(error);
        return;
      }

      /* Hold the worker we actually post to: a later init() may swap
       * this.worker, and the listeners have to come off the same object. */
      const worker = this.worker;
      const requestId = `${this.requestIdPrefix}-${++this.requestCounter}`;
      let timeoutId = null;

      const cleanup = () => {
        worker.removeEventListener("message", handleMessage);
        worker.removeEventListener("error", handleFailure);
        worker.removeEventListener("messageerror", handleFailure);
        signal?.removeEventListener("abort", handleAbort);
        if (timeoutId !== null) clearTimeout(timeoutId);
        this.inFlight.delete(requestId);
        if (key !== undefined && this.inFlightByKey.get(key) === requestId) {
          this.inFlightByKey.delete(key);
        }
      };

      const handleMessage = (e) => {
        const {
          type,
          success,
          result,
          error,
          requestId: responseRequestId,
        } = e.data || {};

        /* Only react to replies for THIS payload */
        const matchesRequestId =
          responseRequestId && responseRequestId === requestId;
        /* Workers cached from before request ids existed reply without one;
         * match those on type so a stale worker still settles the promise. */
        const matchesLegacyType =
          !responseRequestId &&
          (type === payload?.type || type === this.legacyResultType);

        if (!matchesRequestId && !matchesLegacyType) return;

        cleanup();
        success ? resolve(result) : reject(new Error(error || "Worker error"));
      };

      const handleFailure = (event) => {
        cleanup();
        /* A worker-level error belongs to the worker, not to one request: the
         * script failed to load, or the worker died. Drop it so the next call
         * builds a fresh one instead of posting into a corpse forever. */
        if (this.worker === worker) {
          this.worker = null;
          this.workerVersion = null;
        }
        reject(new Error(event?.message || "Worker error"));
      };

      const handleAbort = () => {
        cleanup();
        reject(cancelledError("Worker request aborted"));
      };

      const handleCancel = (reason) => {
        cleanup();
        reject(cancelledError(reason));
      };

      if (Number.isFinite(timeoutMs) && timeoutMs > 0) {
        timeoutId = setTimeout(() => {
          cleanup();
          reject(
            new Error(
              `Worker request ${requestId} timed out after ${timeoutMs}ms`,
            ),
          );
        }, timeoutMs);
      }

      this.inFlight.set(requestId, handleCancel);
      if (key !== undefined) this.inFlightByKey.set(key, requestId);

      worker.addEventListener("message", handleMessage);
      worker.addEventListener("error", handleFailure);
      worker.addEventListener("messageerror", handleFailure);
      signal?.addEventListener("abort", handleAbort);

      try {
        worker.postMessage({ ...payload, requestId }); // send work
      } catch (error) {
        cleanup();
        reject(error);
      }
    });
  }

  // ─── Legacy alias so older code that calls `.calculate()` still works
  calculate(payload, options) {
    return this.run(payload, options);
  }
}

export function createWorkerRequestService(config) {
  return new WorkerRequestService(config);
}
